import { DAY_LABELS, formatServiceTime, type LivestreamBlock } from "./blocks";
import { formatCountdown } from "./livestream-countdown";
import {
  LIVESTREAM_PROVIDER_LABELS,
  livestreamEmbedSource,
  livestreamWatchAddress,
} from "./livestream-sources";

/*
 * When a Live now block is live (ADR 0033, amendment of 2026-10-03). Pure functions of the block, the church's zone
 * and an instant, so the public site decides at each request on its own clock, every theme prints the same words, and
 * page delivery (ADR 0056) caps a page's cache headers at the block's next switch. Nothing here reads the database.
 *
 * The states:
 * - live: the instant is inside a service's window, [opens, start + duration): opening inclusive, end exclusive, the
 *   same rule as a scheduled block's window. A service opens at its start, or the block's open-early minutes before
 *   it (0 to 60; third amendment of 2026-10-03, matching Planning Center's countdown minutes, which "start the stream
 *   a set number of minutes before the episode's start time");
 * - after: not live, and the latest service ended less than the block's replay hours ago (24 by default, 0 for
 *   never): a "Watch the replay" link;
 * - upcoming: otherwise, with the next start.
 *
 * Whenever it is not live, a countdown shows from the block's countdown minutes before the next opening (60 by
 * default), unless the church turned it off (second amendment of 2026-10-03). It counts to the opening: "Starts in
 * 4:32" when the player opens at the start, "Opens in 4:32" when it opens early, so the words never promise the
 * service itself sooner than the church set it.
 *
 * Service times are wall-clock times in the church's zone, or in the zone of the campus the block names
 * (`livestreamPlace`). Each date's start is resolved with that date's own offset,
 * so a 10:00 service is at 10:00 on both sides of a daylight-saving change. A duration is elapsed time: a 90-minute
 * stream that starts at 1:00 on the night the clocks go back still lasts 90 minutes. A start inside the hour skipped
 * in spring resolves to an instant beside the gap rather than failing.
 */

/** How long "Watch the replay" shows after a service ends when the block does not say: 24 hours. */
export const REPLAY_WINDOW_MS = 24 * 60 * 60 * 1000;

/** How long before a start the countdown shows when the block does not say: 60 minutes. */
export const COUNTDOWN_LEAD_MS = 60 * 60 * 1000;

/** The zone every church has unless it set another (`public.tenants.time_zone`'s default). */
export const DEFAULT_TIME_ZONE = "America/New_York";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

type Day = (typeof WEEKDAYS)[number];
type Service = LivestreamBlock["services"][number];

/**
 * What the state machine reads from a block: its services, and the replay and countdown settings, which a block saved
 * before they existed lacks (the contract fills in the defaults; a caller with a raw block gets them here too).
 */
export type LivestreamTiming = Pick<LivestreamBlock, "services"> &
  Partial<
    Pick<LivestreamBlock, "replayHours" | "countdown" | "countdownMinutes" | "openEarlyMinutes">
  >;

/** The countdown's words while the player opens at the start, and while it opens early. */
export const COUNTDOWN_LABEL_START = "Starts in" as const;
export const COUNTDOWN_LABEL_OPEN = "Opens in" as const;

/** How many minutes before each start the player opens: 0 for a block saved before the setting existed. */
function openEarly(block: LivestreamTiming): number {
  return block.openEarlyMinutes ?? 0;
}

function replayMs(block: LivestreamTiming): number {
  return block.replayHours === undefined ? REPLAY_WINDOW_MS : block.replayHours * HOUR_MS;
}

/** The countdown's lead in milliseconds, or null when the block shows none. */
function countdownMs(block: LivestreamTiming): number | null {
  if (block.countdown === false) return null;
  return block.countdownMinutes === undefined
    ? COUNTDOWN_LEAD_MS
    : block.countdownMinutes * MINUTE_MS;
}

/** One occurrence of a service: its instants, and the wall-clock day and time it was entered as. */
export interface LivestreamWindow {
  /** When the block goes live: the start, less the block's open-early minutes. */
  readonly opens: number;
  /** The service's start as the church entered it. */
  readonly start: number;
  readonly end: number;
  readonly day: Day;
  readonly time: string;
}

export type LivestreamState =
  | { readonly kind: "live"; readonly window: LivestreamWindow }
  | {
      readonly kind: "after";
      readonly ended: LivestreamWindow;
      readonly next: LivestreamWindow | null;
    }
  | { readonly kind: "upcoming"; readonly next: LivestreamWindow | null };

// ------------------------------------------------------------------------------------------------ zones

const formatters = new Map<string, Intl.DateTimeFormat>();

/** One formatter per zone, kept: building one is the slow part, and every request asks again. */
function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (f === undefined) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/** The church's zone if the runtime knows it, else the platform default. */
export function usableZone(timeZone: string | undefined): string {
  if (timeZone === undefined || timeZone === "") return DEFAULT_TIME_ZONE;
  try {
    formatter(timeZone);
    return timeZone;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

interface Wall {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

function wallAt(instant: number, timeZone: string): Wall {
  const parts = formatter(timeZone).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
}

/** The zone's offset from UTC at an instant, in milliseconds (positive east of Greenwich). */
function offsetAt(instant: number, timeZone: string): number {
  const w = wallAt(instant, timeZone);
  return (
    Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) -
    Math.floor(instant / 1000) * 1000
  );
}

/** The instant a wall-clock time names in the zone, corrected twice so the date's own offset applies. */
function zonedInstant(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  const first = naive - offsetAt(naive, timeZone);
  return naive - offsetAt(first, timeZone);
}

// ------------------------------------------------------------------------------------------------ windows

/**
 * Every occurrence of the services from nine days before `at` to eight days after, in start order: enough to find the
 * latest service that ended within the longest replay (a week, and a stream lasts at most six hours) and the next start
 * (at most a week away). `openEarlyMinutes` moves each window's opening (not its start) earlier.
 */
export function livestreamWindows(
  services: readonly Service[],
  timeZone: string,
  at: number,
  openEarlyMinutes = 0,
): LivestreamWindow[] {
  const early = openEarlyMinutes * MINUTE_MS;
  const zone = usableZone(timeZone);
  const today = wallAt(at, zone);
  const windows: LivestreamWindow[] = [];
  for (let offset = -9; offset <= 8; offset += 1) {
    // Calendar arithmetic on the local date, in UTC so no zone moves it.
    const date = new Date(Date.UTC(today.year, today.month - 1, today.day + offset));
    const weekday = WEEKDAYS[date.getUTCDay()];
    for (const service of services) {
      if (service.day !== weekday) continue;
      const [h, m] = service.start.split(":").map(Number);
      const start = zonedInstant(
        date.getUTCFullYear(),
        date.getUTCMonth() + 1,
        date.getUTCDate(),
        h ?? 0,
        m ?? 0,
        zone,
      );
      windows.push({
        opens: start - early,
        start,
        end: start + service.durationMinutes * MINUTE_MS,
        day: service.day,
        time: service.start,
      });
    }
  }
  return windows.sort((a, b) => a.opens - b.opens);
}

/** Whether the block is live, just finished, or waiting at `at` (epoch ms), in the zone given (`livestreamPlace`). */
export function livestreamState(
  block: LivestreamTiming,
  timeZone: string | undefined,
  at: number,
): LivestreamState {
  const windows = livestreamWindows(block.services, usableZone(timeZone), at, openEarly(block));
  const live = windows.find((w) => w.opens <= at && at < w.end);
  if (live !== undefined) return { kind: "live", window: live };
  const next = windows.find((w) => w.opens > at) ?? null;
  let ended: LivestreamWindow | null = null;
  for (const w of windows) {
    if (w.end <= at && (ended === null || w.end > ended.end)) ended = w;
  }
  if (ended !== null && at < ended.end + replayMs(block)) return { kind: "after", ended, next };
  return { kind: "upcoming", next };
}

/**
 * The next instant after `at` at which the block may show something else: a countdown appearing, the player opening
 * (the start, or the open-early minutes before it), a stream ending, or a replay link going. Null only for a block with no services, which its contract refuses. Page delivery
 * (ADR 0056) caps a page's `max-age` and `s-maxage` here, so no cache holds the page across a switch.
 */
export function livestreamNextChange(
  block: LivestreamTiming,
  timeZone: string | undefined,
  at: number,
): number | null {
  const replay = replayMs(block);
  const lead = countdownMs(block);
  let next: number | null = null;
  const windows = livestreamWindows(block.services, usableZone(timeZone), at, openEarly(block));
  for (const w of windows) {
    const edges = [w.opens, w.end];
    if (replay > 0) edges.push(w.end + replay);
    if (lead !== null) edges.push(w.opens - lead);
    for (const edge of edges) {
      if (edge > at && (next === null || edge < next)) next = edge;
    }
  }
  return next;
}

// ------------------------------------------------------------------------------------------------ the campus

/** A campus as the block needs it: which one, what it is called, and its zone (absent: the church's). */
export interface LivestreamCampus {
  readonly id: string;
  readonly name: string;
  readonly timeZone?: string | undefined;
}

/** Where a block's times are kept: the zone, and the campus that zone came from (null: the church's own). */
export interface LivestreamPlace {
  readonly timeZone: string | undefined;
  readonly campus: LivestreamCampus | null;
}

/**
 * The zone a block's services are in (ADR 0033, second amendment of 2026-10-03): the zone of the campus the block
 * names, else of the campus whose page this is, else the church's. A campus without a zone of its own keeps the
 * church's; a campus the site does not know (another church's, archived, or a typo) is ignored, so the worst a wrong
 * id does is leave the times in the church's zone.
 */
export function livestreamPlace(
  block: Pick<LivestreamBlock, "campusId">,
  site: {
    readonly timeZone: string | undefined;
    readonly campuses: readonly LivestreamCampus[];
    /** The campus whose page is being drawn, if any (ADR 0036's campus pages). */
    readonly campusId?: string | undefined;
  },
): LivestreamPlace {
  const id = block.campusId ?? site.campusId;
  const campus = id === undefined ? undefined : site.campuses.find((c) => c.id === id);
  if (campus === undefined) return { timeZone: site.timeZone, campus: null };
  const own = campus.timeZone === undefined || campus.timeZone === "" ? undefined : campus.timeZone;
  return { timeZone: own ?? site.timeZone, campus };
}

// ------------------------------------------------------------------------------------------------ words

/** "Sunday 10:00 AM": a window's day and time as the church entered them. */
export function livestreamWhen(window: Pick<LivestreamWindow, "day" | "time">): string {
  return `${DAY_LABELS[window.day]} ${formatServiceTime(window.time)}`;
}

/**
 * The schedule in a line, for the block's card in the editor and under the block on the site: "Sundays 9:00 and
 * 11:00 AM, 90 min". Days in week order, times in order; a shared AM or PM is said once.
 */
export function livestreamSummary(services: readonly Service[]): string {
  if (services.length === 0) return "";
  const days = WEEKDAYS.flatMap((day) => {
    const times = services
      .filter((s) => s.day === day)
      .map((s) => s.start)
      .sort();
    const unique = [...new Set(times)];
    if (unique.length === 0) return [];
    const formatted = unique.map((t) => formatServiceTime(t));
    const meridiem = formatted.map((t) => /\s?(AM|PM)$/i.exec(t)?.[0] ?? "");
    const shared = meridiem.every((m) => m !== "" && m === meridiem[0]);
    const words = shared
      ? formatted.map((t, i) =>
          i === formatted.length - 1 ? t : t.slice(0, t.length - (meridiem[i] ?? "").length),
        )
      : formatted;
    const list =
      words.length === 1
        ? (words[0] ?? "")
        : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1] ?? ""}`;
    return [`${DAY_LABELS[day]}s ${list}`];
  });
  const durations = [...new Set(services.map((s) => s.durationMinutes))].sort((a, b) => a - b);
  const length =
    durations.length === 1
      ? `${String(durations[0])} min`
      : `${String(durations[0])}–${String(durations[durations.length - 1])} min`;
  return `${days.join("; ")}, ${length}`;
}

/** What a theme draws for a Live now block at one instant: the state and every word and address, decided once. */
export interface LivestreamView {
  readonly state: LivestreamState["kind"];
  /**
   * "Starts in 4:32" (or "Opens in 4:32" when the block opens the player early), the words it uses, and the opening it
   * counts to (epoch ms), while the next opening is inside the block's countdown lead and the block is not live; null
   * otherwise, or when the church turned the countdown off.
   */
  readonly countdown: {
    readonly text: string;
    readonly label: typeof COUNTDOWN_LABEL_START | typeof COUNTDOWN_LABEL_OPEN;
    readonly opensAt: number;
  } | null;
  /** "Live now", "Watch the replay" or "Next live: Sunday 10:00 AM". */
  readonly status: string;
  /** "Next live: Sunday 10:00 AM" when not live, else null. */
  readonly next: string | null;
  /** "Live Sundays 9:00 and 11:00 AM, 90 min", and "(North Campus time)" when a campus's zone keeps the times. */
  readonly schedule: string;
  /** The frame address, for the player variant while live; null when the address yields none (draw the button). */
  readonly embedSrc: string | null;
  /** The frame's accessible name. */
  readonly frameTitle: string;
  /** The stream on the provider's own site. */
  readonly watchHref: string;
  /** "Watch live on YouTube". */
  readonly watchLabel: string;
  /** Where "Watch the replay" goes: the church's replay link, else the stream's own address. */
  readonly replayHref: string;
}

/**
 * The block at `at`. `place` is where its times are kept (`livestreamPlace`); a bare zone (or undefined, for the
 * platform default) is the church's own.
 */
export function livestreamView(
  block: LivestreamBlock,
  place: LivestreamPlace | string | undefined,
  at: number,
): LivestreamView {
  const where: LivestreamPlace =
    typeof place === "object" ? place : { timeZone: place, campus: null };
  const state = livestreamState(block, where.timeZone, at);
  const next =
    state.kind === "live" || state.next === null
      ? null
      : `Next live: ${livestreamWhen(state.next)}`;
  const lead = countdownMs(block);
  const label = openEarly(block) > 0 ? COUNTDOWN_LABEL_OPEN : COUNTDOWN_LABEL_START;
  const countdown =
    state.kind !== "live" && state.next !== null && lead !== null && state.next.opens - at <= lead
      ? {
          text: formatCountdown(state.next.opens - at, label),
          label,
          opensAt: state.next.opens,
        }
      : null;
  const summary = `Live ${livestreamSummary(block.services)}`;
  return {
    state: state.kind,
    countdown,
    status:
      state.kind === "live"
        ? "Live now"
        : state.kind === "after"
          ? "Watch the replay"
          : (next ?? ""),
    next,
    schedule: where.campus === null ? summary : `${summary} (${where.campus.name} time)`,
    embedSrc: livestreamEmbedSource(block.provider, block.url, block.channelId),
    frameTitle: `${block.heading}: live stream`,
    watchHref: livestreamWatchAddress(block.provider, block.url),
    watchLabel: `Watch live on ${LIVESTREAM_PROVIDER_LABELS[block.provider]}`,
    replayHref: block.replayUrl ?? livestreamWatchAddress(block.provider, block.url),
  };
}
